import { useCallback } from 'react';
import { Tr, Td } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { useNavigate } from 'react-router-dom';
import { PermitChip } from 'components/elements/PermitChip';
import { StatusChip } from 'components/elements/StatusChip';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { ActivityTable } from 'components/layout/Table/ActivityTable';
import RelatedCompaniesCell from 'pages/ProjectDetails/ActivityList/RelatedCompaniesCell';
import { ProjectActivity } from 'types';
import { trackViewStageDetails } from 'utils/analytics';
import {
  ProjectGroupTypes,
  ViewStageDetailsClickLocations,
} from 'utils/analytics/constants';
import { formatDate } from 'utils/date';
import { renameDocuments } from 'utils/renameDocuments';

type Props = {
  activities: ProjectActivity[];
  city: string;
  projectId?: string;
};

export const ActivityList = ({ activities, city, projectId = '' }: Props) => {
  const navigate = useNavigate();
  const documentType = useCallback(
    (docType: string) => {
      return renameDocuments(docType);
    },
    [city],
  );

  const tableItem = (isClickable: boolean) => ({
    _hover: {
      cursor: isClickable ? 'pointer' : 'default',
      background: 'brand.grey-lighter',
    },
  });

  const idStyle = {
    color: 'brand.text.quiet',
    fontSize: '11px',
  };

  const dateStyle = {
    fontSize: '11px',
    color: 'brand.text.quiet',
  };

  const tableData = {
    whiteSpace: 'nowrap',
  };

  const companyData = {
    whiteSpace: 'pre-wrap',
  };

  const handleActivityClick = (isClickable: boolean, activity: ProjectActivity) => {
    if (isClickable) {
      trackViewStageDetails({
        projectId,
        groupId: String(0),
        groupType: ProjectGroupTypes.UNCATEGORIZED,
        stageId: activity.document_uuid,
        stageType: activity.document_type,
        clickLocation: ViewStageDetailsClickLocations.PDP,
        clickText: undefined,
      });
      if (activity.document_type_code && activity.document_id) {
        navigate(
          `?document-type=${activity.document_type_code}&document-id=${activity.document_id}`,
        );

        return;
      }

      let activityDocType: string;
      if (activity.document_type === 'Rezoning') {
        activityDocType = 'Land Use Redesignation';
      } else if (activity.document_type === 'Electrical Permit') {
        activityDocType = 'Electrical Inspection';
      } else {
        activityDocType = activity.document_type;
      }

      const legacySearch = `?${activityDocType?.split(' ').join('-').toLowerCase()}=${
        activity.document_uuid
      }`;
      navigate(legacySearch);
    }
  };

  return (
    <ActivityTable>
      {activities.map((activity) => {
        const modalIsClickable = Boolean(
          activity?.document_id || activity?.document_uuid,
        );

        return (
          <Tooltip
            shouldWrapChildren={false}
            tooltipText={
              <FlexColumn padding="0.5rem" gridGap={2}>
                {activity.project_name && (
                  <>
                    <Text.BodyBold>Name</Text.BodyBold>
                    {activity.project_name}
                  </>
                )}
                {activity.description && (
                  <>
                    <Text.BodyBold>Description</Text.BodyBold>
                    {activity.description}
                  </>
                )}
              </FlexColumn>
            }
            key={`activity-tooltip-${activity.document_uuid}`}
          >
            <Tr
              key={`activity-list-${activity.document_uuid}`}
              onClick={() => handleActivityClick(modalIsClickable, activity)}
              sx={tableItem(modalIsClickable)}
              className="no-break-on-print"
            >
              <Td sx={tableData}>
                <PermitChip type={documentType(activity.document_type)}>
                  {documentType(activity.document_type)}
                </PermitChip>
              </Td>

              <Td sx={{ ...idStyle, ...tableData }}>
                {activity.permitnum || activity.document_uuid}
              </Td>
              <Td sx={{ ...tableData, ...companyData }}>
                <RelatedCompaniesCell
                  companyList={activity?.companies}
                  relatedContacts={activity?.related_contacts}
                />
              </Td>
              <Td sx={tableData}>
                <StatusChip status={activity.status} fontSize="12px" textAlign="start" />
              </Td>
              <Td sx={{ ...dateStyle, ...tableData }}>{formatDate(activity.date)}</Td>
            </Tr>
          </Tooltip>
        );
      })}
    </ActivityTable>
  );
};
